import { z } from 'zod';

import { IdSchema } from '../../base/primitives';
import {
  TopicActionsSchema,
  TopicContentSchema,
  TopicIsActiveSchema,
  TopicNameSchema,
  TopicReferencesSchema,
} from './knowledge-base.schema';

export const CreateTopicExampleQueriesSchema = z
  .object({
    queries: z
      .array(z.string(), { required_error: 'Queries array is required' })
      .describe(
        'Array of example query strings for this topic. ' +
          'These will be used to help the RAG retrieval system understand when to trigger this topic.',
      ),
  })
  .describe(
    'Object containing an array of example query strings. ' +
      "Format: { queries: ['query1', 'query2', ...] }. " +
      "Example: { queries: ['When will my order arrive?', 'How much is shipping?', 'Can I track my package?'] }",
  );

export const CreateTopicSchema = z
  .object({
    id: IdSchema,
    name: TopicNameSchema,
    content: TopicContentSchema,
    actions: TopicActionsSchema,
    exampleQueries: CreateTopicExampleQueriesSchema,
    references: TopicReferencesSchema,
    isActive: TopicIsActiveSchema.optional(),
  })
  .describe(
    'Create a new topic. Each topic should cover ONE specific concept - create multiple topics instead of bundling FAQs together.',
  );

export const UpdateTopicReferencesSchema =
  TopicReferencesSchema.partial().describe(
    'Partial topic references for update operations. ' +
      'All fields are optional to allow updating only specific references.',
  );

export const UpdateTopicSchema = z
  .object({
    id: IdSchema,
    name: TopicNameSchema.optional(),
    content: TopicContentSchema.optional(),
    actions: TopicActionsSchema.optional(),
    exampleQueries: CreateTopicExampleQueriesSchema.optional(),
    references: UpdateTopicReferencesSchema.optional(),
    isActive: TopicIsActiveSchema.optional(),
  })
  .describe(
    'Command payload for updating an existing topic. ' +
      'All fields except ID are optional to allow partial updates.',
  );

export const DeleteTopicSchema = z
  .object({
    id: IdSchema,
  })
  .describe('Command payload for deleting a topic from the knowledge base');

export type CreateTopicPayload = z.infer<typeof CreateTopicSchema>;
export type UpdateTopicPayload = z.infer<typeof UpdateTopicSchema>;
export type DeleteTopicPayload = z.infer<typeof DeleteTopicSchema>;
export type UpdateTopicReferences = z.infer<typeof UpdateTopicReferencesSchema>;
